import { useState, useEffect } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'
import { AlertTriangle } from 'lucide-react'
import { useTranslation, Trans } from 'react-i18next'

interface TypedConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  documentCount: number
  loading?: boolean
  confirmText?: string
}

export function TypedConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  documentCount,
  loading = false,
  // The typed safety word stays fixed across languages so the help text and muscle memory match.
  confirmText = 'DELETE',
}: TypedConfirmDialogProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false)

  useEffect(() => {
    setIsConfirmEnabled(inputValue === confirmText)
  }, [inputValue, confirmText])

  useEffect(() => {
    if (!isOpen) {
      setInputValue('')
    }
  }, [isOpen])

  const handleConfirm = () => {
    if (isConfirmEnabled && !loading) {
      onConfirm()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">
              {t('typedConfirm.irreversible')}
            </p>
            <p className="text-sm text-red-700 mt-1">
              {message}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <Trans
              i18nKey="typedConfirm.aboutToDelete"
              values={{ count: documentCount }}
              components={{ b: <span className="font-semibold text-gray-900" /> }}
            />
          </p>
        </div>

        <div>
          <label htmlFor="confirm-input" className="block text-sm font-medium text-gray-700 mb-2">
            <Trans
              i18nKey="typedConfirm.typeToConfirm"
              values={{ word: confirmText }}
              components={{ code: <span className="font-mono font-semibold text-red-600" /> }}
            />
          </label>
          <input
            id="confirm-input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder={confirmText}
            disabled={loading}
            autoComplete="off"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || loading}
            loading={loading}
          >
            {loading ? t('typedConfirm.deleting') : t('typedConfirm.deleteDocuments')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
